import React, { useState, useEffect } from 'react';
import { Search, ExternalLink, FileText, Image, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import './AdminDashboard.css';

export default function RegisteredDatabase() {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  // --- PAGINATION STATE ---
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/candidates/registered-users`);
      
      const data = Array.isArray(response.data) ? response.data : (response.data.candidates || response.data.users || []);
      setUsers(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching registered users", error);
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to permanently delete this candidate?")) {
      return;
    }
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/candidates/delete/${userId}`);
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
      alert("Candidate deleted successfully.");
    } catch (error) {
      console.error("Error deleting user:", error);
      alert("Failed to delete candidate.");
    }
  };

  const getFileLink = (path) => {
    if (!path) return null;
    return `${process.env.REACT_APP_API_BASE_URL}/api/candidates/view-file?url=${encodeURIComponent(path)}`;
};

  // 1. FILTER
  const filteredUsers = users.filter(user => 
    (user.fullName && user.fullName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (user.skills && user.skills.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // 2. PAGINATE
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const formatDate = (dateString) => dateString ? new Date(dateString).toLocaleDateString() : '--';
  const displayVal = (val) => val || '--';

  return (
    // 1. FIX MAIN CONTAINER HEIGHT: This prevents the window scrollbar!
    <div className="content-card" style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: 'calc(100vh - 120px)', // Adjust this if your header is taller/shorter
        overflow: 'hidden',            // Hides the "page" scrollbar
        paddingBottom: '0' 
    }}>
      
      {/* HEADER SECTION (Fixed at top) */}
      <div className="section-header" style={{
          display:'flex', 
          justifyContent:'space-between', 
          marginBottom:'15px', 
          alignItems:'center', 
          flexShrink: 0 
      }}>
        <h3>Registered Candidate Portal Database</h3>
        <div style={{position:'relative'}}>
          <input 
            type="text" 
            placeholder="Search Name, Email, Skills..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{padding:'10px 35px 10px 12px', borderRadius:'6px', border:'1px solid #e2e8f0', minWidth:'300px'}}
          />
          <Search size={18} style={{position:'absolute', right:'10px', top:'10px', color:'#94a3b8'}} />
        </div>
      </div>

      {/* 2. TABLE CONTAINER: SCROLLBAR MOVES HERE */}
      <div className="table-container" style={{ 
          overflow: 'auto',       // Shows scrollbars ONLY when needed inside this box
          flex: 1,                // Fills remaining space
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          background: 'white'
      }}>
        {loading ? (
            <div style={{padding:'40px', textAlign:'center', color:'#64748b'}}>Loading Candidates...</div>
        ) : (
        <table className="modern-table" style={{minWidth:'2200px', width: '100%', borderCollapse: 'separate', borderSpacing: 0}}>
          <thead style={{position: 'sticky', top: 0, zIndex: 30}}>
            <tr style={{background:'#f8fafc'}}>
              <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Registered On</th>
              {/* --- STICKY FULL NAME HEADER --- */}
              <th className="sticky-name-col" style={{
                  position:'sticky', left:0, background:'#f8fafc', zIndex:40, 
                  padding:'12px', borderBottom:'1px solid #e2e8f0', borderRight:'2px solid #e2e8f0', minWidth:'180px'
              }}>
                  Full Name
              </th>
              
              <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Email</th>
              <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Phone</th>
              <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Username</th>
              <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>LinkedIn</th>
              <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Skills</th>
              <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Total Exp</th>
              <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Relevant Exp</th>
              <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Current Org</th>
              <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Current CTC</th>
              <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Current Location</th>
              <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Preferred Loc</th>
              <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Serving Notice?</th>
              <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>LWD</th>
              <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Resume</th>
              <th style={{padding:'12px', borderBottom:'1px solid #e2e8f0'}}>Photo</th>
              
              
              {/* --- STICKY ACTIONS HEADER --- */}
              <th className="sticky-actions-col" style={{
                  position:'sticky', right:0, background:'#f8fafc', zIndex:40, textAlign:'center', 
                  padding:'12px', borderBottom:'1px solid #e2e8f0', borderLeft:'2px solid #e2e8f0'
              }}>
                  Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.length > 0 ? (
              currentUsers.map(user => (
              <tr key={user.id} style={{background:'#fff', borderBottom:'1px solid #f1f5f9'}}>
                {/* --- STICKY FULL NAME CELL --- */}
                <td style={{padding:'12px', color:'#64748b', fontSize:'0.85rem'}}>{formatDate(user.createdAt)}</td>
                <td className="sticky-name-col" style={{
                    position:'sticky', left:0, background:'#fff', zIndex:30, 
                    fontWeight:'600', borderRight:'2px solid #e2e8f0', color:'#1e293b', padding:'12px'
                }}>
                  {displayVal(user.fullName)}
                </td>
                
                
                <td style={{padding:'12px'}}>{displayVal(user.email)}</td>
                <td style={{padding:'12px'}}>{displayVal(user.phone || user.contactNumber)}</td>
                <td style={{padding:'12px'}}>{displayVal(user.username)}</td>
                <td style={{padding:'12px'}}>
                  {user.linkedin ? (
                    <a href={user.linkedin} target="_blank" rel="noreferrer" style={{color:'#0284c7', display:'flex', alignItems:'center', gap:'4px', textDecoration:'none'}}>
                      <ExternalLink size={14} /> Link
                    </a>
                  ) : '--'}
                </td>
                <td style={{padding:'12px'}}><div style={{maxWidth:'200px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}} title={user.skills}>{displayVal(user.skills)}</div></td>
                <td style={{padding:'12px'}}>{displayVal(user.experience || user.totalExperience)}</td>
                <td style={{padding:'12px'}}>{displayVal(user.relevantExperience)}</td>
                <td style={{padding:'12px'}}>{displayVal(user.presentOrganization || user.currentCompany)}</td>
                <td style={{padding:'12px'}}>{displayVal(user.currentCTC)}</td>
                <td style={{padding:'12px'}}>{displayVal(user.currentLocation || user.location)}</td>
                <td style={{padding:'12px'}}>{displayVal(user.preferredLocation)}</td>
                <td style={{padding:'12px'}}>
                    {user.servingNotice ? 
                        <span style={{background:'#fff7ed', color:'#c2410c', padding:'2px 6px', borderRadius:'4px', fontSize:'12px', fontWeight:'600'}}>Yes</span> 
                        : 'No'}
                </td>
                <td style={{padding:'12px'}}>{formatDate(user.lastWorkingDay)}</td>
                <td style={{padding:'12px'}}>
                  {getFileLink(user.resumePath) ? (
                    <a href={getFileLink(user.resumePath)} target="_blank" rel="noreferrer" style={{color:'#2563eb', display:'flex', alignItems:'center', gap:'4px', textDecoration:'none'}}>
                        <FileText size={14} /> View
                    </a>
                  ) : <span style={{color:'#cbd5e1'}}>--</span>}
                </td>
                <td style={{padding:'12px'}}>
                   {getFileLink(user.photoPath) ? (
                    <a href={getFileLink(user.photoPath)} target="_blank" rel="noreferrer" style={{color:'#2563eb', display:'flex', alignItems:'center', gap:'4px', textDecoration:'none'}}>
                        <Image size={14} /> View
                    </a>
                  ) : <span style={{color:'#cbd5e1'}}>--</span>}
                </td>
                

                {/* --- STICKY ACTIONS CELL --- */}
                <td className="sticky-actions-col" style={{
                    position:'sticky', right:0, background:'#fff', zIndex:30, 
                    borderLeft:'2px solid #e2e8f0', textAlign:'center', padding:'12px'
                }}>
                    <button 
                        onClick={() => handleDelete(user.id)}
                        style={{
                            background: '#fee2e2', 
                            border: 'none', 
                            cursor: 'pointer', 
                            color: '#ef4444',
                            padding: '6px',
                            borderRadius: '4px',
                            transition: 'background 0.2s'
                        }}
                        title="Delete Candidate"
                    >
                        <Trash2 size={16} />
                    </button>
                </td>
              </tr>
            ))
            ) : (
                <tr>
                    <td colSpan="18" style={{textAlign:'center', padding:'20px', color:'#94a3b8'}}>No candidates found.</td>
                </tr>
            )}
          </tbody>
        </table>
        )}
      </div>

      {/* PAGINATION CONTROLS (Fixed at bottom) */}
      <div style={{
          display:'flex', 
          justifyContent:'space-between', 
          alignItems:'center', 
          padding:'15px 0', 
          borderTop:'1px solid #e2e8f0',
          flexShrink: 0
      }}>
          <span style={{color:'#64748b', fontSize:'0.9rem'}}>
            Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredUsers.length)} of {filteredUsers.length} candidates
          </span>
          
          <div style={{display:'flex', gap:'10px', alignItems:'center'}}>
              <button 
                onClick={handlePrevPage} 
                disabled={currentPage === 1}
                style={{
                    display:'flex', alignItems:'center', gap:'5px',
                    padding:'8px 12px', border:'1px solid #e2e8f0', borderRadius:'6px',
                    background: currentPage === 1 ? '#f1f5f9' : '#fff',
                    color: currentPage === 1 ? '#cbd5e1' : '#1e293b',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                  <ChevronLeft size={16} /> Prev
              </button>
              
              <span style={{fontWeight:'600', color:'#334155'}}>
                  Page {currentPage} of {totalPages || 1}
              </span>

              <button 
                onClick={handleNextPage} 
                disabled={currentPage === totalPages || totalPages === 0}
                style={{
                    display:'flex', alignItems:'center', gap:'5px',
                    padding:'8px 12px', border:'1px solid #e2e8f0', borderRadius:'6px',
                    background: (currentPage === totalPages || totalPages === 0) ? '#f1f5f9' : '#fff',
                    color: (currentPage === totalPages || totalPages === 0) ? '#cbd5e1' : '#1e293b',
                    cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer'
                }}
              >
                  Next <ChevronRight size={16} />
              </button>
          </div>
      </div>

    </div>
  );
}
